import { CACHE_NAME, METADATA_CACHE_NAME, IMAGE_CACHE_NAME } from '../config/cache.config';
import { createLogger } from '@hunting-nature/shared-core/logging';

const log = createLogger('CacheTools');

/**
 * Cache Management Utilities
 *
 * Emergency utilities for managing cache in development/production.
 * These can be called from browser console when cache issues occur.
 */

/**
 * Clear all cache data (emergency reset)
 *
 * Usage in browser console:
 * ```
 * // Clear all Secacam caches
 * clearSecacamCache()
 * ```
 */
export async function clearSecacamCache(): Promise<void> {
  try {
    const cacheNames = await caches.keys();
    const secacamCaches = cacheNames.filter(name => name.includes('secacam') || name.includes('api-cache'));

    log.info('Clearing cache entries', { cacheNames: secacamCaches });

    for (const cacheName of secacamCaches) {
      await caches.delete(cacheName);
      log.info('Deleted cache', { cacheName });
    }

    log.info('All Secacam caches cleared, reload the page to start fresh');
  } catch (error) {
    log.error('Failed to clear cache', { error });
  }
}

/**
 * List all cached URLs
 */
export async function listCachedUrls(): Promise<Array<string>> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();
    const urls = requests.map(request => request.url);

    log.info('Cached URLs', { urls });
    return urls;
  } catch (error) {
    log.error('Failed to list cached URLs', { error });
    return [];
  }
}

/**
 * Clear cache for a specific URL pattern
 *
 * @param pattern - String or RegExp to match URLs
 */
export async function clearCacheByPattern(pattern: string | RegExp): Promise<number> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const metadataCache = await caches.open(METADATA_CACHE_NAME);
    const requests = await cache.keys();

    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let deletedCount = 0;

    for (const request of requests) {
      if (regex.test(request.url)) {
        await cache.delete(request);
        await metadataCache.delete(request);
        deletedCount++;
        log.info('Deleted cache entry', { url: request.url });
      }
    }

    log.info('Cleared cache entries', { deletedCount });
    return deletedCount;
  } catch (error) {
    log.error('Failed to clear cache by pattern', { error });
    return 0;
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  totalEntries: number;
  totalSize: number;
}> {
  try {
    const buckets = [CACHE_NAME, METADATA_CACHE_NAME, IMAGE_CACHE_NAME];
    let totalEntries = 0;
    let totalSize = 0;

    for (const bucketName of buckets) {
      const cache = await caches.open(bucketName);
      const requests = await cache.keys();
      totalEntries += requests.length;
      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const sizeHeader = response.headers.get('x-cached-size');
          if (sizeHeader) {
            totalSize += Number(sizeHeader);
          } else {
            const clone = response.clone();
            const blob = await clone.blob();
            totalSize += blob.size;
          }
        }
      }
    }

    const stats = {
      totalEntries,
      totalSize,
    };

    log.info('Cache statistics (all buckets)', {
      totalEntries: stats.totalEntries,
      totalSize: stats.totalSize,
      totalSizeMb: Number((stats.totalSize / 1024 / 1024).toFixed(2)),
    });

    return stats;
  } catch (error) {
    log.error('Failed to get cache stats', { error });
    return { totalEntries: 0, totalSize: 0 };
  }
}

/**
 * Validate cache health
 */
export async function validateCacheHealth(): Promise<void> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const metadataCache = await caches.open(METADATA_CACHE_NAME);
    const requests = await cache.keys();

    let corruptedCount = 0;
    let errorCount = 0;

    log.info('Validating cache health');

    for (const request of requests) {
      const response = await cache.match(request);
      const metadata = await metadataCache.match(request);

      if (!response) {
        log.warn('Missing response', { url: request.url });
        corruptedCount++;
        continue;
      }

      if (!metadata) {
        log.warn('Missing metadata', { url: request.url });
        corruptedCount++;
        continue;
      }

      // Check if cached response is an error
      if (response.status >= 400) {
        log.warn('Cached error response', { status: response.status, url: request.url });
        errorCount++;
        // Auto-delete error responses
        await cache.delete(request);
        await metadataCache.delete(request);
      }
    }

    log.info('Validation complete', { corruptedCount, errorCount });

    if (corruptedCount > 0 || errorCount > 0) {
      log.warn('Consider running clearSecacamCache() for a fresh start');
    }
  } catch (error) {
    log.error('Failed to validate cache', { error });
  }
}

// Expose utilities to window for console access
if (globalThis.window !== undefined) {
  (globalThis as any).clearSecacamCache = clearSecacamCache;
  (globalThis as any).listCachedUrls = listCachedUrls;
  (globalThis as any).clearCacheByPattern = clearCacheByPattern;
  (globalThis as any).getCacheStats = getCacheStats;
  (globalThis as any).validateCacheHealth = validateCacheHealth;

  log.debug('Cache utilities loaded', {
    commands: ['clearSecacamCache()', 'listCachedUrls()', 'clearCacheByPattern(pattern)', 'getCacheStats()', 'validateCacheHealth()'],
  });
}
